import { z } from 'zod';
import { companyNumberSchema } from './client';

export const companiesHouseProfileUrlSchema = z.string().max(200).regex(
  /^https:\/\/find-and-update\.company-information\.service\.gov\.uk\/company\/[A-Za-z0-9]{8}\/?$/,
  'Paste an official HTTPS Companies House company profile link without query or fragment',
);
export const companiesHousePreviewInputSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('create'), profileUrl: companiesHouseProfileUrlSchema }).strict(),
  z.object({ mode: z.literal('edit'), clientId: z.string().uuid(), profileUrl: companiesHouseProfileUrlSchema }).strict(),
]);
export const registryDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Invalid calendar date');
const filingSchema = z.object({
  nextDue: registryDateSchema.nullable(),
  periodStart: registryDateSchema.nullable(),
  periodEnd: registryDateSchema.nullable(),
  overdue: z.boolean().nullable(),
});
export const companiesHousePreviewOutputSchema = z.object({
  companyNumber: companyNumberSchema,
  companyName: z.string().max(1000),
  sourceUrl: companiesHouseProfileUrlSchema,
  fetchedAt: z.string().datetime(),
  dateOfCreation: registryDateSchema.nullable(),
  registryStatus: z.string().max(200).nullable(),
  registeredOfficeAddress: z.string().max(4000).nullable(),
  registryJurisdiction: z.string().max(200).nullable(),
  companyType: z.string().max(200).nullable(),
  sicCodes: z.array(z.string().max(100)).max(50),
  industry: z.string().max(100).nullable().default(null),
  activities: z.array(z.object({ code: z.string().regex(/^\d{5}$/), description: z.string().max(1000) })).max(50).default([]),
  accounts: filingSchema,
  confirmationStatement: z.object({ nextDue: registryDateSchema.nullable(), madeUpTo: registryDateSchema.nullable(), overdue: z.boolean().nullable() }),
  suggestions: z.object({
    companyNumber: companyNumberSchema,
    company: z.string().max(200).nullable(),
    legalName: z.string().max(300).nullable(),
    incorporationDate: z.string().datetime().nullable(),
    address: z.string().max(500).nullable(),
    country: z.literal('GB').nullable(),
    jurisdiction: z.literal('UK').nullable(),
  }),
  warnings: z.array(z.string().max(500)).max(50),
});
export type CompaniesHousePreviewInput = z.infer<typeof companiesHousePreviewInputSchema>;
export type CompaniesHousePreview = z.infer<typeof companiesHousePreviewOutputSchema>;
export type CompaniesHouseSuggestions = CompaniesHousePreview['suggestions'];

export const companiesHouseOnboardingInputSchema = z.object({ profileUrl: companiesHouseProfileUrlSchema }).strict();
export const registryDirectorsSchema = z.object({
  directors: z.array(z.object({ id: z.string().regex(/^[a-f0-9]{64}$/), name: z.string().min(1).max(200), role: z.enum(['director', 'nominee-director']) }).strict()).max(500),
  complete: z.boolean(),
  warning: z.string().max(500).nullable(),
});
export const registryDuplicatesSchema = z.object({
  visible: z.array(z.object({ id: z.string().uuid(), clientCode: z.string(), name: z.string(), company: z.string().nullable() })),
  hidden: z.boolean(),
});
export const companiesHouseOnboardingOutputSchema = z.object({
  company: companiesHousePreviewOutputSchema,
  officers: registryDirectorsSchema,
  duplicates: registryDuplicatesSchema,
});
export type RegistryDirectors = z.infer<typeof registryDirectorsSchema>;
export type CompaniesHouseOnboarding = z.infer<typeof companiesHouseOnboardingOutputSchema>;
